/**
 * 这是一个查询框自动布局组件
 * 1. 当所有表单项超出一行时, 自动隐藏超出的项
 * 2. 所有表单项刚好够一行时, 隐藏'展开'按钮
 * 3. 表单项不满一行时, 自动靠右对齐
 * 4. 靠右对齐时可能表单项会显得比较大, 可以加属性进行调整:
 * columns={6} labelCol={{span: 8}}
 */
import React, {useMemo, useState} from 'react';
import {FormInner, FormInnerProps} from '../FormInner';
import {Button, Space} from 'antd';

import styles from './index.module.scss';
import {DownOutlined, SearchOutlined, UndoOutlined} from '@ant-design/icons';
import classNames from 'classnames';
import {FormFieldProps} from '../FormField';
import {DefaultColumnSize} from '../../context';

interface FormQueryProps<T> extends FormInnerProps<T> {
    showReset?: boolean; // 是否显示重置按钮
    extra?: React.ReactNode; // 跟在折叠按钮后方随便什么东西
    lines?: number; // 默认可见的行数
    defaultExpand?: boolean; // 默认展开
}

export function FormQuery<T>({
    children,
    lines = 1,
    showReset,
    defaultExpand,
    extra,
    className,
    ...formProps
}: FormQueryProps<T>) {
    // 默认不展开
    const [expand, setExpand] = useState(defaultExpand);
    const {columns = DefaultColumnSize} = formProps;

    const formItems = React.Children.toArray(children);

    // 始终显示的表单项
    const {constantItems, hiddenItems, columnCount} = useMemo(() => {
        const constantItems: unknown[] = [];
        const hiddenItems: unknown[] = [];
        let columnCount = 1;

        formItems.forEach((item: any) => {
            if (!React.isValidElement<FormFieldProps>(item))
                throw new Error(`${item}不是一个合法的表单项`);

            columnCount += item.props.colSize || 1;

            if (columnCount > columns * lines) {
                hiddenItems.push(React.cloneElement(item, {preserve: false}));
            } else {
                constantItems.push(React.cloneElement(item, {preserve: true}));
            }
        });

        return {constantItems, hiddenItems, columnCount};
    }, [formItems, columns, lines]);

    const alignRight = columnCount < columns;

    return (
        <div
            className={classNames(styles.root, className, {
                [styles.alignRight]: alignRight,
            })}>
            <FormInner {...formProps}>
                {alignRight && <div className={styles.spacer} />}

                {constantItems}
                {expand && hiddenItems}

                <Space align="start" className={styles.buttons}>
                    <Button
                        htmlType="submit"
                        type="primary"
                        icon={<SearchOutlined />}>
                        查询
                    </Button>

                    {showReset && (
                        <Button htmlType="reset" icon={<UndoOutlined />}>
                            重置
                        </Button>
                    )}

                    {hiddenItems.length !== 0 && (
                        <Button
                            className={classNames({
                                [styles.expandButton]: expand,
                            })}
                            type="link"
                            onClick={() => setExpand(!expand)}>
                            {expand ? '收起' : '展开'} <DownOutlined />
                        </Button>
                    )}

                    {extra}
                </Space>
            </FormInner>
        </div>
    );
}

FormQuery.defaultProps = {
    showReset: true,
    colon: false,
    defaultExpand: false,
};
